/**
 * Landing Page Navbar Component
 *
 * Responsive navigation bar for the public institutional website.
 * Features: Logo, navigation links, mobile menu, CTA buttons.
 */

import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Container,
  useScrollTrigger,
  Slide,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { landingColors, landingShadows } from '../../theme/landingTheme';
import Logo from './Logo';
import { trackTrialStart, buildSignupUrl } from '../../utils/analytics';

// ============================================================================
// Types
// ============================================================================

interface NavItem {
  label: string;
  path: string;
}

interface HideOnScrollProps {
  children: React.ReactElement;
}

// ============================================================================
// Navigation Items
// ============================================================================

const navItems: NavItem[] = [
  { label: 'Funcionalidades', path: '/funcionalidades' },
  { label: 'Planos', path: '/planos' },
  { label: 'Blog', path: '/blog' },
  { label: 'Sobre', path: '/sobre' },
  { label: 'Contato', path: '/contato' },
];

// ============================================================================
// Hide on Scroll Component
// ============================================================================

function HideOnScroll({ children }: HideOnScrollProps) {
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

// ============================================================================
// Navbar Component
// ============================================================================

const Navbar: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Check if current page is landing (for transparent navbar)
  const isLandingPage = location.pathname === '/';

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setScrolled(offset > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  // Dynamic styles based on scroll and page
  // Always use solid background and dark colors for visibility
  const navbarBgColor = scrolled
    ? 'rgba(255, 255, 255, 0.98)'
    : isLandingPage
      ? 'transparent'
      : 'rgba(255, 255, 255, 0.98)';

  // On landing page (not scrolled), use white text over teal hero
  // Otherwise use dark text
  const textColor = isLandingPage && !scrolled
    ? landingColors.neutral.white
    : landingColors.neutral.gray800;

  // Mobile Drawer Content
  const drawer = (
    <Box sx={{ width: 280, pt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          mb: 2,
        }}
      >
        <Box
          component={Link}
          to="/"
          sx={{
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
          }}
          onClick={() => setMobileOpen(false)}
        >
          <Logo variant="dark" size="sm" />
        </Box>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>

      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname === item.path}
              sx={{
                py: 1.5,
                px: 3,
                '&.Mui-selected': {
                  bgcolor: alpha(landingColors.primary.main, 0.08),
                  '&:hover': {
                    bgcolor: alpha(landingColors.primary.main, 0.12),
                  },
                },
              }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: location.pathname === item.path ? 600 : 400,
                  color: location.pathname === item.path
                    ? landingColors.primary.main
                    : landingColors.neutral.gray700,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box sx={{ px: 2, mt: 3 }}>
        <Button
          fullWidth
          variant="text"
          size="small"
          onClick={() => handleNavigation('/portal/login')}
          sx={{
            mb: 1,
            color: landingColors.neutral.gray600,
            fontSize: '0.85rem',
            '&:hover': {
              bgcolor: alpha(landingColors.primary.main, 0.04),
            },
          }}
        >
          Área do Paciente
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => handleNavigation('/login')}
          sx={{
            mb: 2,
            borderColor: landingColors.primary.main,
            color: landingColors.primary.main,
            '&:hover': {
              borderColor: landingColors.primary.dark,
              bgcolor: alpha(landingColors.primary.main, 0.04),
            },
          }}
        >
          Entrar
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            trackTrialStart('navbar_mobile');
            handleNavigation(buildSignupUrl('/signup', { trial: 'true' }));
          }}
          sx={{
            bgcolor: landingColors.primary.main,
            '&:hover': {
              bgcolor: landingColors.primary.dark,
            },
          }}
        >
          Testar Gratis
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Skip to content link for accessibility */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          zIndex: 9999,
          '&:focus': {
            position: 'fixed',
            top: 8,
            left: 8,
            width: 'auto',
            height: 'auto',
            overflow: 'visible',
            bgcolor: landingColors.primary.main,
            color: landingColors.neutral.white,
            px: 2,
            py: 1,
            borderRadius: 1,
            fontWeight: 600,
            fontSize: '0.9rem',
            textDecoration: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          },
        }}
      >
        Pular para o conteúdo principal
      </Box>
      <HideOnScroll>
        <AppBar
          position="fixed"
          elevation={scrolled ? 1 : 0}
          sx={{
            bgcolor: navbarBgColor,
            backdropFilter: scrolled ? 'blur(10px)' : 'none',
            boxShadow: scrolled ? landingShadows.md : 'none',
            transition: 'all 0.3s ease-in-out',
          }}
        >
          <Container maxWidth="xl">
            <Toolbar
              sx={{
                justifyContent: 'space-between',
                minHeight: { xs: 64, md: 72 },
                px: { xs: 0, sm: 2 },
              }}
            >
              {/* Logo */}
              <Box
                component={Link}
                to="/"
                sx={{
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Logo
                  variant={isLandingPage && !scrolled ? 'light' : 'colored'}
                  size="xxl"
                />
              </Box>

              {/* Desktop Navigation */}
              {!isMobile && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {navItems.map((item) => (
                    <Button
                      key={item.path}
                      component={Link}
                      to={item.path}
                      sx={{
                        color: textColor,
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        '&:hover': {
                          bgcolor: scrolled || !isLandingPage
                            ? alpha(landingColors.primary.main, 0.08)
                            : alpha(landingColors.neutral.white, 0.1),
                        },
                        '&::after': location.pathname === item.path ? {
                          content: '""',
                          position: 'absolute',
                          bottom: 4,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 20,
                          height: 2,
                          bgcolor: scrolled || !isLandingPage
                            ? landingColors.primary.main
                            : landingColors.neutral.white,
                          borderRadius: 1,
                        } : {},
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Box>
              )}

              {/* Desktop CTA Buttons */}
              {!isMobile && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    component={Link}
                    to="/portal/login"
                    variant="text"
                    size="small"
                    sx={{
                      color: textColor,
                      fontWeight: 400,
                      fontSize: '0.85rem',
                      opacity: 0.85,
                      '&:hover': {
                        opacity: 1,
                        bgcolor: scrolled || !isLandingPage
                          ? alpha(landingColors.primary.main, 0.08)
                          : alpha(landingColors.neutral.white, 0.1),
                      },
                    }}
                  >
                    Área do Paciente
                  </Button>
                  <Button
                    component={Link}
                    to="/login"
                    variant="text"
                    sx={{
                      color: textColor,
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: scrolled || !isLandingPage
                          ? alpha(landingColors.primary.main, 0.08)
                          : alpha(landingColors.neutral.white, 0.1),
                      },
                    }}
                  >
                    Entrar
                  </Button>
                  <Button
                    onClick={() => {
                      trackTrialStart('navbar');
                      navigate(buildSignupUrl('/signup', { trial: 'true' }));
                    }}
                    variant="contained"
                    sx={{
                      bgcolor: scrolled || !isLandingPage
                        ? landingColors.primary.main
                        : landingColors.neutral.white,
                      color: scrolled || !isLandingPage
                        ? landingColors.neutral.white
                        : landingColors.primary.main,
                      fontWeight: 600,
                      px: 3,
                      boxShadow: landingShadows.button,
                      '&:hover': {
                        bgcolor: scrolled || !isLandingPage
                          ? landingColors.primary.dark
                          : landingColors.neutral.gray100,
                        boxShadow: landingShadows.buttonHover,
                      },
                    }}
                  >
                    Testar Gratis
                  </Button>
                </Box>
              )}

              {/* Mobile Menu Button */}
              {isMobile && (
                <IconButton
                  edge="end"
                  onClick={handleDrawerToggle}
                  sx={{
                    color: textColor,
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Toolbar spacer - only on non-landing pages */}
      {!isLandingPage && (
        <Toolbar sx={{ minHeight: { xs: 64, md: 72 } }} />
      )}
    </>
  );
};

export default Navbar;
